/* istanbul ignore file */
import Konva from 'konva';
import { KonvaEventObject, Node, NodeConfig } from 'konva/types/Node';
import { KonvaPointerEvent } from 'konva/types/PointerEvents';
import { Box } from 'konva/types/shapes/Transformer';
import { Vector2d } from 'konva/types/types';
import { Transform } from 'konva/types/Util';
import { baseTheme } from 'styles/utils';
import { Coordinates, MakeGetRestrictedPosition, MEDIA_GROUP_ID } from './types';

/**
 * Restrict position to its container
 *
 * @param {object} coords {x, y} coords
 * @param {object} containerDimensions container with {width, height}
 * @param {object} dimensions (optional) dimensions to account for when restricting position
 * @returns {object} new {x,y} coords that do not exceed container
 */
export const makeGetRestrictedPosition: MakeGetRestrictedPosition = ({ containerDimensions, dimensions = { width: 0, height: 0 }, scale = 1 }) => {
  const upperBound = 0;
  const leftBound = 0;
  const rightBound = containerDimensions.width - dimensions.width;
  const bottomBound = containerDimensions.height - dimensions.height;

  return (coords) => {
    // restrict point between bounds
    let xPos = Math.min(Math.max(coords.x, leftBound), rightBound * scale);
    let yPos = Math.min(Math.max(coords.y, upperBound), bottomBound * scale);

    xPos = xPos < 0.03 ? 0 : xPos;
    yPos = yPos < 0.03 ? 0 : yPos;
    return {
      x: xPos,
      y: yPos,
    };
  };
};

/**
 * Get the relative position of a pointer
 *
 * @param {*} e pointer pointer event
 * @param {*} invertedTransform transform to apply
 * @param {number} scale scale of the zoom
 * @return {{x: number, y: number}}
 */
export const getRelativePointerPosition = (target: Konva.Stage, invertedTransform: Transform, scale: number = 1): { x: number; y: number } => {
  // get pointer (say mouse or touch) position
  const pos = target.getPointerPosition();
  if (!pos) return { x: 0, y: 0 };
  // now we can find relative point
  const transformedPoint = invertedTransform.point(pos as Vector2d);

  return {
    x: transformedPoint.x / scale,
    y: transformedPoint.y / scale,
  };
};

/**
 * Check if event is within media area
 * @param {import('konva/types/PointerEvents').KonvaEventObject<KonvaPointerEvent>} e konva pointer event
 */
export const isWithinMediaArea = (e: KonvaEventObject<KonvaPointerEvent>): boolean => {
  let currentNode: Konva.Node | null = e.target;
  let found = false;

  while (currentNode !== null && !found) {
    found = currentNode.id() === MEDIA_GROUP_ID;
    currentNode = currentNode?.parent;
  }

  return found;
};

/**
 * Inverts the absolute transform of the current Konva node.
 * This can be applied to recreate the transform on other Konva nodes.
 *
 * @param {*} target konva pointer event
 */
export const getInvertedTransform = (target: Node<NodeConfig>): Transform => {
  const transform = target?.getAbsoluteTransform()?.copy();

  transform?.invert();
  return transform;
};

/**
 * Convenience function for getting inverted transform of the media group
 * @param {import('konva/types/PointerEvents').KonvaPointerEvent} e konva pointer event
 */
export const getMediaGroupInvertedTransform = (e: KonvaEventObject<KonvaPointerEvent>): Transform => {
  const results = (e.currentTarget as Konva.Stage)?.find(`#${MEDIA_GROUP_ID}`);
  if (results === undefined || results.length === 0) throw new Error(`Unable to locate Konva Group with id '${MEDIA_GROUP_ID}'`);
  const mediaGroup = results[0];
  return getInvertedTransform(mediaGroup);
};

export interface Circle {
  radius: number;
  fill: string;
  strokeWidth: number;
  x: number;
  y: number;
}

const makeCircle = (props: { x: number; y: number; isFirst?: boolean }, scale: number): Circle => ({
  x: props.x,
  y: props.y,
  radius: props.isFirst ? 7 / scale : 5 / scale,
  fill: props.isFirst ? baseTheme.light.colors.primary : baseTheme.light.colors.white,
  strokeWidth: 1 / scale,
});

/**
 * Create circles with coordinates based on points array
 */
export function makeCirclesFromPoints(points: [number, number][], scale: number): Circle[] {
  return points.map(([x, y], index) => makeCircle({ x, y, isFirst: index === 0 }, scale));
}

/**
 * Check if two points are close to each other
 */
export function checkIfIsInVicinity(coord1: Coordinates, coord2: Coordinates, scale: number): boolean {
  if (Math.abs(Math.hypot(coord1.x - coord2.x, coord1.y - coord2.y)) < 10 / scale) {
    return true;
  }
  return false;
}

export const getNodeDimensions = (node: Konva.Node): { width: number; height: number } => ({
  width: node.width(),
  height: node.height(),
});

export const getNodeCoords = (node: Konva.Node): { x: number; y: number } => ({
  x: node.x(),
  y: node.y(),
});

/**
 *
 * @param num - number to round
 * @param scale - scale to round to
 * @returns - rounded number
 */
export const roundNumber = (num: number, scale = 10000): number => Math.round(num * scale) / scale;

/**
 *
 * @param r1 - Rectangle or Polygon
 * @param r2 - Container inside which r1 should be
 * @param buffer - Buffer to add to the area of r1
 * @returns - boolean
 */
export function hasBoxExceeded(r1: Omit<Box, 'rotation'>, r2: Omit<Box, 'rotation'>, buffer = 1000): boolean {
  const { x: boxX, y: boxY, width: boxWidth, height: boxHeight } = r1;
  const { x: containerX, y: containerY, width: containerWidth, height: containerHeight } = r2;

  // Calculate the right edge of the box and the container
  const boxRightEdge = boxX + boxWidth;
  const containerRightEdge = containerX + containerWidth;

  // Calculate the left edge of the box and the container
  const boxLeftEdge = boxX;
  const containerLeftEdge = containerX;

  // Calculate the bottom edge of the box and the container
  const boxBottomEdge = boxY + boxHeight;
  const containerBottomEdge = containerY + containerHeight;

  // Calculate the top edge of the box and the container
  const boxTopEdge = boxY;
  const containerTopEdge = containerY;

  // Calculate the overlap along the x-axis by finding the minimum of the right edges and subtracting the maximum of the left edges,
  // ensuring that the result is at least 0 to handle cases where there's no overlap.
  const xOverlap = Math.max(0, Math.min(boxRightEdge, containerRightEdge) - Math.max(boxLeftEdge, containerLeftEdge));

  // Calculate the overlap along the y-axis by finding the minimum of the bottom edges and subtracting the maximum of the top edges,
  // ensuring that the result is at least 0 to handle cases where there's no overlap.
  const yOverlap = Math.max(0, Math.min(boxBottomEdge, containerBottomEdge) - Math.max(boxTopEdge, containerTopEdge));

  const overlapArea = xOverlap * yOverlap;
  const isNewAreaExceeding = roundNumber(overlapArea) < roundNumber(getArea(r1)) - buffer;

  return !overlapArea || isNewAreaExceeding;
}

export function getArea(rect: { width: number; height: number }): number {
  return rect.width * rect.height;
}

// https://github.com/math-utils/area-polygon/blob/master/index.js
export const getPolygonArea = (points: { x: number; y: number }[], isSigned: boolean = false): number => {
  const l = points.length;
  let det = 0;

  if (points[0] !== points[points.length - 1]) points = points.concat(points[0]);

  for (let i = 0; i < l; i += 1) det += points[i].x * points[i + 1].y - points[i].y * points[i + 1].x;
  return isSigned ? det / 2 : Math.abs(det) / 2;
};

export function getBboxArea(bbox: CF.API.BoundingBox): number {
  const { top_row, bottom_row, left_col, right_col } = bbox;
  return Math.abs((bottom_row - top_row) * (right_col - left_col));
}
